import { FC, useCallback, useEffect, useState } from 'react';

import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Button,
  Fade,
  FormHelperText,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  useTheme,
  Theme,
  SelectChangeEvent,
} from '@mui/material';
import { FieldArray, getIn, useFormik, FormikProvider } from 'formik';
import debounce from 'just-debounce-it';
import { toast } from 'react-hot-toast';
import Scroll from 'react-scroll';
import * as yup from 'yup';
import { RequiredStringSchema } from 'yup/lib/string';
import { AnyObject } from 'yup/lib/types';
import shallow from 'zustand/shallow';

import { createSubmission } from '../../../api/submission';
import { useAnalytics } from '../../../hooks/dataTracking/useAnalytics';
import { X as XIcon } from '../../../icons/x';
import { ISubmissionTags, submissionTags } from '../../../interfaces/submission';
import { useAuthStore } from '../../../stores/auth';
import { processErrorMessage } from '../../../utils/processErrorMessage';
import { QuillEditor } from '../../utils/quill-editor';

export interface NewProposalModalProps {
  open: boolean;
  handleClose: () => void;
}

interface IProposalSection {
  // Shortname of field (matches name used on backend)
  id: string;
  // Human readable title for frontend display
  title: string;
  // A short description of the content that this field should contain
  description: string;
  // If defined the field will validate when empty OR set to this value
  defaultValue?: string;
  // The minimum length of this field (overwrites the default 50)
  overrideLength?: number;
}

const proposalSections: Array<IProposalSection> = [
  {
    id: 'problem',
    title: 'Problem',
    description: 'The raw idea, a use case, or something we’ve seen that motivates us to work on this',
  },
  {
    id: 'appetite',
    title: 'Appetite',
    description: 'How much time we want to spend and how that constrains the solution',
  },
  {
    id: 'solution',
    title: 'Solution',
    description:
        'The core elements we came up with, presented in a form that’s easy for people to immediately understand',
  },
  {
    id: 'rabbitHoles',
    title: 'Rabbit holes',
    description: 'Details about the solution worth calling out to avoid problems',
  },
  {
    id: 'noGos',
    title: 'No-gos',
    description:
        'Anything specifically excluded from the concept: functionality or use cases we intentionally aren’t covering (optional)',
    defaultValue: "<p>N/A</p>", // TODO: Remove tags when validation is updated to not rely on them
  },
];

// TODO: Derive this from proposalSections[].id
type SectionType = 'problem' | 'appetite' | 'solution' | 'rabbitHoles' | 'noGos';

const getYupProposalSection = () => {
  const yupSections: { [key: string]: RequiredStringSchema<string | undefined, AnyObject> } = {};
  proposalSections.forEach((proposal) => {
    if (proposal) {
      yupSections[proposal.id] = yup.string().default(proposal.defaultValue)
          .required(`${proposal.title} is required`)
          .test(
              'length-ignoring-defaults',
              `${proposal.title} must have a minimum of ${proposal.overrideLength || 50} characters`,
              (value) => !(value && value != proposal.defaultValue && value.length < (proposal.overrideLength || 57)) as boolean
          ) // TODO: Reduce length from 57 to 50 after validation is configured to strip tags
    }
  });
  return yupSections;
};

function getStyles(tag: ISubmissionTags, tags: readonly ISubmissionTags[], theme: Theme) {
  return {
    fontWeight: tags.indexOf(tag) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
  };
}

export const NewProposalModal: FC<NewProposalModalProps> = ({ open, handleClose }) => {
  const theme = useTheme();
  const { sendButtonClickTrackingEvent } = useAnalytics();

  const { storedToken, getUser } = useAuthStore(({ storedToken, getUser }) => ({ storedToken, getUser }), shallow);
  const [savedFormInitialized, setsavedFormInitialized] = useState(false);

  const formik = useFormik({
    initialValues: {
      problem: '',
      summary: '',
      appetite: '',
      solution: '',
      rabbitHoles: '',
      noGos: '',
      title: '',
      tags: [] as ISubmissionTags[],
      submit: null,
      voteOptions: ['', ''],
    },
    validationSchema: yup.object({
      title: yup
          .string()
          .min(8, 'Title must have a minimum of 8 characters')
          .max(300, "Title can't be longer than 300 characters")
          .required('A Title is required'),
      summary: yup.string().min(50, 'Summary must have a minimum of 50 characters').required('A Summary is required'),
      tags: yup.array().min(1, 'A minimum of one Tag is required').required('At least one Tag is required'),
      voteOptions: yup.array().of(yup.string().required('Voting option is required')),
      ...getYupProposalSection(),
    }),
    onSubmit: async (values, helpers): Promise<void> => {
      try {
        sendButtonClickTrackingEvent('Submit proposal');

        if (storedToken) {

          // Set any missing form elements to their defaults
          // Validation assures that only those fields that have defaults will be empty here
          proposalSections.forEach((proposal) => {
            if (proposal) {
              const sectionId = proposal.id as SectionType
              values[sectionId] = values[sectionId] || proposal.defaultValue || ""
            }
          });

          const { submit, ...submissionValues } = values;
          await createSubmission(storedToken, submissionValues);
          toast.success('Proposal created!');
          // get User Submissions to Update Submissions List
          getUser(storedToken);
          handleClear();
          handleClose();
        } else {
          toast.error('Wallet not connected or verified!');
        }
      } catch (err) {
        if (err) {
          const error = processErrorMessage(err);
          helpers.setErrors({ submit: error });
          if (error.includes('Forbidden')) toast.error('Wallet not connected or verified!');
          else toast.error(error || 'Something went wrong!');
        } else {
          toast.error('An unexpected error has occurred!');
        }
        helpers.setStatus({ success: false });
        helpers.setSubmitting(false);
      }
    },
  });

  const autoSave = useCallback(
      debounce((formValue: string) => {
        toast('Draft Auto-Saved!', {
          icon: '💾',
        });
        localStorage.setItem('proposalForm', formValue);
      }, 5000),
      [],
  );

  useEffect(() => {
    const localProposalItem = localStorage.getItem('proposalForm');
    const proposalFormStoredValues = localProposalItem ? JSON.parse(localProposalItem) : {};

    formik.setValues({
      ...formik.values,
      ...proposalFormStoredValues,
    });
    setsavedFormInitialized(true);
  }, []);

  useEffect(() => {
    // This useEffect was called even if the Modal was closed
    // This will cause un-necessary saving of forms and notification that the draft was saved
    if (!open) return;

    if (savedFormInitialized) {
      const formikValues = formik.values;
      const isFormEmpty =
          (Object.keys(formikValues) as Array<keyof typeof formikValues>).filter(
              (key) =>
                  formikValues[key] !== formik.initialValues[key] &&
                  formikValues[key] !== '<p><br></p>' &&
                  formikValues[key] !== null,
          ).length === 0;

      if (!isFormEmpty) {
        autoSave(JSON.stringify(formik.values));
      } else {
        autoSave.cancel();
        handleClear();
      }
    }
  }, [autoSave, formik.values, savedFormInitialized, open]);

  const handleClear = () => {
    localStorage.removeItem('proposalForm');
    formik.resetForm();
    sendButtonClickTrackingEvent('Clear proposal form');
  };

  useEffect(() => {
    if (!formik.isValid && formik.submitCount !== 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const scroller = Scroll.scroller;
      if (firstErrorKey) {
        scroller.scrollTo(firstErrorKey, {
          duration: 1000,
          delay: 100,
          smooth: true,
          containerId: 'FormModal',
          offset: -100,
        });
      }
    }
  }, [formik.submitCount, formik.isValid, formik.errors, formik.isSubmitting]);

  return (
      <Modal
          open={open}
          onClose={handleClose}
          closeAfterTransition
          BackdropProps={{
            timeout: 100,
          }}
      >
        <form onSubmit={formik.handleSubmit} style={{ height: '100%' }}>
          <Fade in={open}>
            <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: { xs: 0, md: 4 },
                }}
            >
              <Paper
                  elevation={12}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    margin: { xs: 0, md: 3 },
                    minHeight: 500,
                    maxHeight: '100%',
                    outline: 'none',
                    width: '100%',
                    maxWidth: 740,
                  }}
              >
                <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      p: 3,
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                >
                  <Typography variant="h6">Submit Proposal</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton onClick={() => {
                    handleClose();
                    sendButtonClickTrackingEvent('Close new proposal form via x icon');
                  }}>
                    <XIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                    sx={{
                      px: 3,
                      py: 3,
                      flexGrow: 1,
                      overflow: 'auto',
                    }}
                    id="FormModal"
                >
                  <TextField
                      fullWidth
                      id="title"
                      name="title"
                      label="Title"
                      sx={{ mb: 2, borderRadius: 1 }}
                      InputProps={{ sx: { backgroundColor: 'background.default' } }}
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      error={formik.touched.title && Boolean(formik.errors.title)}
                      helperText={formik.touched.title && formik.errors.title}
                  />
                  <TextField
                      fullWidth
                      id="summary"
                      name="summary"
                      label="Summary"
                      multiline
                      rows={4}
                      sx={{ mb: 2, borderRadius: 1 }}
                      InputProps={{ sx: { backgroundColor: 'background.default' } }}
                      value={formik.values.summary}
                      onChange={formik.handleChange}
                      error={formik.touched.summary && Boolean(formik.errors.summary)}
                      helperText={formik.touched.summary && formik.errors.summary}
                  />
                  <FormControl sx={{ width: '100%', mb: 4 }}>
                    <InputLabel>Tags</InputLabel>
                    <Select
                        multiple
                        id="tags"
                        name="tags"
                        value={formik.values.tags}
                        error={formik.touched.tags && Boolean(formik.errors.tags)}
                        onChange={(event: SelectChangeEvent<typeof formik.values.tags>) => {
                          const {
                            target: { value },
                          } = event;

                          formik.setFieldValue('tags', typeof value === 'string' ? value.split(',') : value);
                        }}
                        input={<OutlinedInput label="Tags" sx={{ backgroundColor: 'background.default' }} />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                  <Chip key={value} label={value} />
                              ))}
                            </Box>
                        )}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 224,
                            },
                          },
                        }}
                    >
                      {submissionTags.map((tag) => (
                          <MenuItem
                              key={tag.value}
                              value={tag.value}
                              style={getStyles(tag.value, formik.values.tags, theme)}
                          >
                            {tag.value}
                          </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.tags && formik.errors.tags && (
                        <FormHelperText error>{formik.errors.tags}</FormHelperText>
                    )}
                  </FormControl>
                  <Divider />
                  {proposalSections.map((section) => {
                    const sectionId = section.id as SectionType;
                    return (
                        <Box key={section.id}>
                          <Typography
                              color="textSecondary"
                              sx={{
                                mb: 2,
                                mt: 4,
                              }}
                              variant="subtitle1"
                          >
                            {section.title}
                          </Typography>
                          <QuillEditor
                              id={sectionId}
                              onChange={(value: string): void => {
                                formik.setFieldValue(section.id, value);
                              }}
                              placeholder={section.description}
                              value={formik.values[sectionId]}
                              error={formik.touched[sectionId] && formik.errors[sectionId]}
                              sx={{
                                height: 300,
                                flexGrow: 1,
                              }}
                          />
                          {formik.touched[sectionId] && formik.errors[sectionId] && (
                              <FormHelperText error sx={{ ml: 2 }}>
                                {formik.errors[sectionId]}
                              </FormHelperText>
                          )}
                        </Box>
                    );
                  })}
                  <Divider sx={{ my: 4 }} />
                  <FormikProvider value={formik}>
                    <FieldArray name="voteOptions">
                      {({ push, remove }) => (
                          <Box id="voteOptions">
                            {formik.values.voteOptions.map((voteOption, index) => {
                              const voteOptionInput = `voteOptions[${index}]`;
                              const touchedVoteOption = getIn(formik.touched, voteOptionInput);
                              const errorVoteOption = getIn(formik.errors, voteOptionInput);

                              return (
                                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <TextField
                                        margin="normal"
                                        variant="outlined"
                                        label={`Vote Option ${index + 1}`}
                                        name={voteOptionInput}
                                        value={voteOption}
                                        helperText={touchedVoteOption && errorVoteOption ? errorVoteOption : ''}
                                        error={Boolean(touchedVoteOption && errorVoteOption)}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        sx={{ width: '100%', mr: 2 }}
                                        inputProps={{ sx: { backgroundColor: 'background.default', borderRadius: 1 } }}
                                    />
                                    {formik.values.voteOptions.length > 2 && (
                                        <IconButton
                                            aria-label="delete"
                                            size="medium"
                                            color="error"
                                            onClick={() => remove(index)}
                                            sx={{ borderRadius: 50, mt: 3 }}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                    )}
                                  </Box>
                              );
                            })}
                            <Box>
                              <Button
                                  type="button"
                                  variant="outlined"
                                  color="secondary"
                                  onClick={() => push('')}
                                  sx={{ mt: 2 }}
                              >
                                Add Option
                              </Button>
                            </Box>
                          </Box>
                      )}
                    </FieldArray>
                  </FormikProvider>
                </Box>
                <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'space-between',
                      p: 2,
                      borderTop: 1,
                      borderColor: 'divider',
                    }}
                >
                  <Button variant="outlined" onClick={handleClear}>
                    Clear
                  </Button>
                  <Button variant="contained" type="submit">
                    Submit
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Fade>
        </form>
      </Modal>
  );
};
